import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import type { AgentApiJobChangeProcessesRecord } from '@/types/serializers';
import { TextField } from '@/components/TextField';
import CandidatesSelectColorMenu from '@/features/agent/candidates/components/CandidatesSelectColorMenu';
import type { Color } from '@/features/agent/candidates/types/CandidateBoard';
import useSubmenuSide from '@/hooks/useSubmenuSide';
import JobChangeProcessBoardCard from './JobChangeProcessBoardCard';

const MENU_ITEMS = [
  { option: 'nameEdit', label: '名前変更' },
  { option: 'colorEdit', label: 'カラーを変更' },
  { option: 'delete', label: '削除' },
] as const;

type Props = {
  droppableId: string;
  title: string;
  colorType: Color;
  jobChangeProcesses: AgentApiJobChangeProcessesRecord[];
  selectedId?: number;
  onSelect: (jobChangeProcess: AgentApiJobChangeProcessesRecord) => void;
  boardId?: number;
  onTitleChange?: (newTitle: string) => void;
  onColorChange?: (color: Color) => void;
  onDelete?: (boardId: number) => void;
  totalCount?: number;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
};

export default function JobChangeProcessBoardColumn({
  droppableId,
  title,
  colorType,
  jobChangeProcesses,
  selectedId,
  onSelect,
  boardId,
  onTitleChange,
  onColorChange,
  onDelete,
  totalCount,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
}: Props) {
  const isUncategorized = droppableId === 'uncategorized';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const colorMenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { submenuSideClass, resolveSide } = useSubmenuSide();

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  // メニュー外のクリックでメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isColorMenuOpen &&
        colorMenuRef.current &&
        !colorMenuRef.current.contains(event.target as Node)
      ) {
        setIsColorMenuOpen(false);
      }

      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isColorMenuOpen]);

  const handleScroll = () => {
    if (!containerRef.current || !onLoadMore || isLoadingMore || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - (scrollTop + clientHeight) < 200) {
      onLoadMore();
    }
  };

  const boardClasses = clsx(
    'w-[272px] min-w-[272px] rounded-sm px-sm py-md shadow-layer1 transition-colors duration-200',
    colorType === 'gray' && 'bg-[#EAEAEA]',
    colorType === 'green' && 'bg-green-50',
    colorType === 'blue' && 'bg-blue-50',
    colorType === 'yellow_green' && 'bg-lime-50',
    colorType === 'yellow' && 'bg-yellow-50',
    colorType === 'orange' && 'bg-[#FFF7EC]',
    colorType === 'red' && 'bg-red-50',
    colorType === 'pink' && 'bg-magenta-50',
    colorType === 'purple' && 'bg-purple-50'
  );

  const handleMenuClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsColorMenuOpen(false);

    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <div className="relative">
          <div ref={provided.innerRef} {...provided.droppableProps} className={boardClasses}>
            <div className="mb-md flex h-xl items-center">
              {isEditingTitle ? (
                <div className="w-[206px]">
                  <TextField
                    ref={textFieldRef}
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={() => {
                      setIsEditingTitle(false);
                      if (onTitleChange && titleValue !== title) {
                        onTitleChange(titleValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        textFieldRef.current?.blur();
                      }
                    }}
                    height={28}
                  />
                </div>
              ) : (
                <p className="line-clamp-1 max-w-[216px] text-heading-sm">{title}</p>
              )}

              {totalCount !== 0 && !isEditingTitle && (
                <span className="ml-sm flex h-lg min-w-lg items-center justify-center rounded-full bg-primary px-xs text-heading-3xs text-white">
                  {totalCount}
                </span>
              )}

              {!isUncategorized && boardId && (
                <div className="relative ml-auto">
                  <div
                    ref={menuButtonRef}
                    role="button"
                    tabIndex={0}
                    className={clsx(
                      'flex size-icon-md cursor-pointer items-center justify-center rounded-sm hover:bg-gray_darken',
                      isMenuOpen && 'bg-gray_darken'
                    )}
                    onClick={handleMenuClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleMenuClick(e);
                      }
                    }}
                  >
                    <img
                      src="/agent/candidates/icon-more.svg"
                      width={24}
                      height={24}
                      alt="メニュー"
                    />
                  </div>

                  {isMenuOpen && (
                    <div
                      ref={menuRef}
                      className="absolute top-full right-0 z-[101] mt-xxs flex w-[150px] cursor-pointer flex-col rounded-sm bg-white shadow-layer3"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      role="menu"
                    >
                      {MENU_ITEMS.map((item) => (
                        <div key={item.option} className="relative">
                          <div
                            className="flex h-xxl items-center px-sm hover:bg-gray_darken"
                            role="menuitem"
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.option === 'nameEdit') {
                                setIsEditingTitle(true);
                                setIsMenuOpen(false);
                                setIsColorMenuOpen(false);
                                setTimeout(() => {
                                  textFieldRef.current?.focus();
                                }, 0);
                              } else if (item.option === 'colorEdit') {
                                resolveSide(menuRef.current);
                                setIsColorMenuOpen(true);
                              } else if (item.option === 'delete' && onDelete) {
                                setIsMenuOpen(false);
                                setIsColorMenuOpen(false);
                                onDelete(boardId);
                              }
                            }}
                          >
                            <p
                              className={clsx(
                                'text-body-xs',
                                item.option === 'delete' && 'text-danger'
                              )}
                            >
                              {item.label}
                            </p>
                            {item.option === 'colorEdit' && (
                              <img
                                src="/agent/candidates/icon-keyboard-arrow-right.svg"
                                width={16}
                                height={16}
                                alt=""
                                className="ml-auto"
                              />
                            )}
                          </div>

                          {item.option === 'colorEdit' && isColorMenuOpen && (
                            <div
                              ref={colorMenuRef}
                              className={clsx('absolute top-0 z-[102]', submenuSideClass)}
                            >
                              <CandidatesSelectColorMenu
                                colorType={colorType}
                                onColorSelect={(color) => {
                                  setIsColorMenuOpen(false);
                                  setIsMenuOpen(false);
                                  if (onColorChange) {
                                    onColorChange(color);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              ref={containerRef}
              className="hidden-scrollbar -m-1 flex max-h-[calc(100vh_-_273px)] flex-col gap-xs overflow-y-auto overscroll-y-contain p-1"
              onScroll={handleScroll}
            >
              {jobChangeProcesses.length === 0
                ? !isLoadingMore && (
                    <div className="flex min-h-[104px] items-center justify-center rounded-sm bg-white drop-shadow-[0px_0px_3px_var(--color-shadow)]">
                      <p className="text-heading-xxs text-main opacity-50">データがありません</p>
                    </div>
                  )
                : jobChangeProcesses.map((jobChangeProcess, index) => (
                    <JobChangeProcessBoardCard
                      key={jobChangeProcess.id}
                      jobChangeProcess={jobChangeProcess}
                      index={index}
                      isSelected={selectedId === jobChangeProcess.id}
                      onSelect={onSelect}
                    />
                  ))}
              {provided.placeholder}

              {isLoadingMore && (
                <div className="flex h-[50px] items-center justify-center">
                  <div className="size-icon-xs animate-spin rounded-full border-2 border-solid border-primary border-t-transparent"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Droppable>
  );
}
